import { type JSX, useEffect, useId, useState } from 'react';

import { AppLink, useNavigator } from '../../discovery/router';
import { appStoreListings, routes } from '../../routes';
import { useSession } from '../../session/SessionProvider';
import { BackLink, LoginLink } from '../panels';
import {
  type HelpContent,
  type HelpQuestion,
  type HelpTopic,
  type HelpTopicId,
  SHIPPED_HELP,
  getHelp,
  recentAppointments,
  recentOrders,
} from './help-api';
import {
  type HelpRow,
  appointmentRow,
  isSearch,
  orderRow,
  pickAppointments,
  pickOrders,
  searchHelp,
  topicFrom,
} from './help-model';
import './help.css';

/**
 * The Help page — `Just Treated Help.dc.html`, website stage 11.
 *
 * Public: a visitor who is not logged in sees the frame's "Can we help with an
 * order?" with Log in, plus any orders this browser placed as a guest; a
 * signed-in person sees "Which order do you need help with?" over their own
 * recent orders and appointments, each linking to the page that can act on it.
 * Then the frame's topics and answers (owner-editable, `content/help`), a
 * search across them, and the 999 / NHS 111 notice.
 *
 * ## What the frame draws that the platform cannot back
 *
 * - The frame's fallback demo orders ("High Street Pharmacy, JT-84712") are
 *   never drawn: no orders means the page says so.
 * - **Chat with us**, **Send feedback**, the **app store** buttons and the
 *   **social** links have no channel, no published app and no account behind
 *   them: drawn in place, inert, "Coming soon".
 * - **Email us** is a working `mailto:` only once the owner sets a mailbox in
 *   the dashboard; until then it is "Coming soon" too. The frame's `help@`
 *   address is not shown — nothing shows it exists.
 * - Answers that would state an undecided time or amount say what the platform
 *   does and that the rest is "to be confirmed" (D11; see `help-schema.ts`).
 * - The frame's "Voucher cards" topic is "Rewards — PENNY points" (D8).
 */
export function HelpPage(): JSX.Element {
  const navigator = useNavigator();
  const [content, setContent] = useState<HelpContent>(SHIPPED_HELP);
  const linked = topicFrom(content, navigator.location.query.get('topic'));
  const [open, setOpen] = useState(linked !== undefined);
  const [topic, setTopic] = useState<HelpTopicId | undefined>(linked);

  useEffect(() => {
    let live = true;
    getHelp()
      .then((published) => {
        // A response with no topics at all would empty the page; the shipped copy stays instead.
        if (live && published.topics.length > 0) setContent(published);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  // A link to `?topic=` opens that topic, including from the page itself.
  useEffect(() => {
    if (linked === undefined) return;
    setOpen(true);
    setTopic(linked);
  }, [linked]);

  return (
    <div className="jt-help">
      <BackLink />
      <OrderPanel />
      <button
        id="help-topics"
        type="button"
        className={`jt-help-else${open ? ' jt-help-else--open' : ''}`}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        Can we help with something else?
        <Chevron flipped={open} />
      </button>
      {open ? (
        <Topics content={content} topic={topic} onTopic={(id) => setTopic(topic === id ? undefined : id)} />
      ) : null}
      <div className="jt-help-safety" role="note">
        <Icon d="M12 3.5 2.5 20h19L12 3.5ZM12 10v4.5M12 17.5h.01" stroke="#a81f1f" size={20} width={2.2} />
        <span>
          Need urgent medical help? Call <b>999</b> in an emergency, or <b>NHS 111</b> if it’s urgent but not
          life-threatening. Our support team can’t give medical advice.
        </span>
      </div>
      <Extras />
    </div>
  );
}

// -- Which order? ----------------------------------------------------------------

interface Rows {
  readonly state: 'loading' | 'ready' | 'failed';
  readonly orders: readonly HelpRow[];
  readonly appointments: readonly HelpRow[];
}

const LOADING: Rows = { state: 'loading', orders: [], appointments: [] };

function useRows(who: 'signedIn' | 'signedOut' | undefined, personId: string | undefined): Rows {
  const [rows, setRows] = useState<Rows>(LOADING);

  useEffect(() => {
    if (who === undefined) return;
    let live = true;
    setRows(LOADING);
    const orders = recentOrders().then((found) => pickOrders(found).map((order) => orderRow(order)));
    const appointments =
      who === 'signedIn'
        ? recentAppointments((items) => pickAppointments(items))
            .then((found) => found.map(({ appointment, listing }) => appointmentRow(appointment, listing)))
            .catch(() => [] as readonly HelpRow[])
        : Promise.resolve([] as readonly HelpRow[]);
    Promise.all([orders, appointments])
      .then(([o, a]) => {
        if (live) setRows({ state: 'ready', orders: o, appointments: a });
      })
      .catch(() => {
        if (live) setRows({ state: 'failed', orders: [], appointments: [] });
      });
    return () => {
      live = false;
    };
  }, [who, personId]);

  return rows;
}

function OrderPanel(): JSX.Element {
  const session = useSession();
  const signedIn = session.status === 'signedIn' && session.user !== undefined;
  const settled = signedIn
    ? 'signedIn'
    : session.status === 'signedOut' || session.status === 'unreachable'
      ? 'signedOut'
      : undefined;
  const rows = useRows(settled, session.user?.personId);

  if (settled === undefined) {
    return (
      <section className="jt-help-card" aria-busy="true">
        <h1 className="jt-help-h1">Can we help with an order?</h1>
        <p className="jt-help-copy">Checking whether you’re logged in…</p>
      </section>
    );
  }

  if (settled === 'signedOut') {
    const guest = rows.state === 'ready' ? rows.orders : [];
    return (
      <section className="jt-help-card jt-help-card--start">
        <h1 className="jt-help-h1">Can we help with an order?</h1>
        <p className="jt-help-copy">
          Log in for your order status, delivery time, appointment details or if you need a hand with
          anything.
        </p>
        <LoginLink className="jt-help-login">Log in</LoginLink>
        {guest.length > 0 ? (
          <div className="jt-help-guest">
            <h2 className="jt-help-h3">Orders on this device</h2>
            <RowList rows={guest} />
          </div>
        ) : null}
      </section>
    );
  }

  const all = [...rows.orders, ...rows.appointments];
  return (
    <section className="jt-help-card">
      <div className="jt-help-head">
        <h1 className="jt-help-h1">Which order do you need help with?</h1>
        <p className="jt-help-copy jt-help-copy--tight">
          Choose an order or appointment to see its status and what you can do next.
        </p>
      </div>
      {rows.state === 'loading' ? (
        <p className="jt-help-quiet" aria-busy="true">
          Loading your orders…
        </p>
      ) : rows.state === 'failed' ? (
        <p className="jt-help-quiet" role="alert">
          We couldn’t load your orders just now. You can still open them from All orders.
        </p>
      ) : all.length === 0 ? (
        <p className="jt-help-quiet">You have no orders or appointments yet.</p>
      ) : (
        <RowList rows={all} />
      )}
      <div className="jt-help-actions">
        <AppLink to={routes.orders.path} className="jt-help-pill jt-help-pill--ink">
          All orders
        </AppLink>
        <AppLink to={routes.bookings.path} className="jt-help-pill jt-help-pill--white">
          Help with an appointment
        </AppLink>
      </div>
    </section>
  );
}

function RowList({ rows }: { readonly rows: readonly HelpRow[] }): JSX.Element {
  return (
    <ul className="jt-help-rows">
      {rows.map((row) => (
        <li key={row.key}>
          <AppLink to={row.href} className="jt-help-row">
            <span className="jt-help-row__tile" style={{ background: row.tile }} aria-hidden="true">
              {row.initials}
            </span>
            <span className="jt-help-row__text">
              <span className="jt-help-row__title">{row.title}</span>
              <span className="jt-help-row__line">{row.line}</span>
            </span>
            <span className="jt-help-row__go">Get help</span>
          </AppLink>
        </li>
      ))}
    </ul>
  );
}

// -- Topics, answers and search -----------------------------------------------------

const TOPIC_ICONS: Readonly<Record<HelpTopicId, string>> = {
  orders: 'M3.5 6.5h11v9h-11zM14.5 9.5h4l3 3v3h-7M7 18.5a1.7 1.7 0 1 0 0-.01M17 18.5a1.7 1.7 0 1 0 0-.01',
  rx: 'M9 3h6v4H9zM6 5h12v16H6zM9 12h6M12 9v6',
  appt: 'M4.5 6h15v14h-15zM4.5 10h15M8.5 3.5v4M15.5 3.5v4',
  pay: 'M3.5 6.5h17v11h-17zM3.5 10h17M7 14.5h4',
  rewards:
    'M20 12v9H4v-9M2 7h20v5H2zM12 21V7M12 7H7.5a2.5 2.5 0 1 1 0-5C11 2 12 7 12 7ZM12 7h4.5a2.5 2.5 0 1 0 0-5C13 2 12 7 12 7Z',
  account: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM4.5 20a7.5 7.5 0 0 1 15 0',
  safety: 'M12 3.2 5 6v5.4c0 4.4 3 8 7 9.4 4-1.4 7-5 7-9.4V6ZM12 9v4M12 16h.01',
  partners: 'M4 21V4h11v17M15 9h5v12M8 8h3M8 12h3M8 16h3',
};

function Topics({
  content,
  topic,
  onTopic,
}: {
  readonly content: HelpContent;
  readonly topic: HelpTopicId | undefined;
  readonly onTopic: (id: HelpTopicId) => void;
}): JSX.Element {
  const [query, setQuery] = useState('');
  const searching = isSearch(query);
  const hits = searching ? searchHelp(content, query) : [];
  const current = content.topics.find((t) => t.id === topic);

  return (
    <div className="jt-help-topics">
      <label className="jt-help-search">
        <Icon d="M11 17.5a6.5 6.5 0 1 0 0-13 6.5 6.5 0 0 0 0 13ZM20 20l-4.2-4.2" stroke="#5b2ede" size={18} />
        <span className="jt-visually-hidden">Search help</span>
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search help, for example “refund”"
          autoComplete="off"
        />
      </label>

      {searching ? (
        <section className="jt-help-panel" aria-label="Search results">
          <h2 className="jt-help-h2" aria-live="polite">
            {hits.length === 0
              ? `No answers match “${query.trim()}”`
              : `${String(hits.length)} ${hits.length === 1 ? 'answer' : 'answers'} for “${query.trim()}”`}
          </h2>
          {hits.length === 0 ? (
            <p className="jt-help-quiet">Try another word, or choose a topic below.</p>
          ) : (
            <Answers
              key={query}
              items={hits.map((hit) => ({ question: hit.question, topic: hit.topic.label }))}
              firstOpen={false}
            />
          )}
          <StillNeedHelp content={content} />
        </section>
      ) : null}

      <div className="jt-help-tiles" role="group" aria-label="Help topics">
        {content.topics.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`jt-help-tile${t.id === topic ? ' jt-help-tile--on' : ''}`}
            aria-pressed={t.id === topic}
            onClick={() => onTopic(t.id)}
          >
            <span className="jt-help-tile__icon" aria-hidden="true">
              <Icon d={TOPIC_ICONS[t.id]} stroke="#5b2ede" size={18} />
            </span>
            <span className="jt-help-tile__text">
              <span className="jt-help-tile__label">{t.label}</span>
              {t.sub !== '' ? <span className="jt-help-tile__sub">{t.sub}</span> : null}
            </span>
          </button>
        ))}
      </div>

      {current !== undefined ? <TopicPanel key={current.id} topic={current} content={content} /> : null}
    </div>
  );
}

function TopicPanel({
  topic,
  content,
}: {
  readonly topic: HelpTopic;
  readonly content: HelpContent;
}): JSX.Element {
  return (
    <section className="jt-help-panel" aria-labelledby={`help-topic-${topic.id}`}>
      <h2 id={`help-topic-${topic.id}`} className="jt-help-h2">
        {topic.label}
      </h2>
      <Answers items={topic.questions.map((question) => ({ question }))} firstOpen />
      <StillNeedHelp content={content} />
    </section>
  );
}

/** The frame's accordion: one answer open at a time; the first open when a topic is chosen. */
function Answers({
  items,
  firstOpen,
}: {
  readonly items: readonly { readonly question: HelpQuestion; readonly topic?: string }[];
  readonly firstOpen: boolean;
}): JSX.Element {
  const [open, setOpen] = useState(firstOpen ? 0 : -1);
  const base = useId();

  return (
    <div className="jt-help-qs">
      {items.map(({ question, topic }, index) => {
        const isOpen = open === index;
        const answerId = `${base}-a${String(index)}`;
        return (
          <div key={`${topic ?? ''}:${question.q}`} className="jt-help-q">
            <button
              type="button"
              className="jt-help-q__btn"
              aria-expanded={isOpen}
              aria-controls={answerId}
              onClick={() => setOpen(isOpen ? -1 : index)}
            >
              <span className="jt-help-q__text">
                {topic !== undefined ? <span className="jt-help-q__topic">{topic}</span> : null}
                {question.q}
              </span>
              <Chevron flipped={isOpen} small />
            </button>
            {isOpen ? (
              <p id={answerId} className="jt-help-q__a">
                {question.a}
              </p>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function StillNeedHelp({ content }: { readonly content: HelpContent }): JSX.Element {
  return (
    <div className="jt-help-still">
      <span className="jt-help-still__label">Still need help?</span>
      <span className="jt-help-pill jt-help-pill--ink jt-help-pill--off" aria-disabled="true">
        Chat with us <Soon />
      </span>
      {content.contactEmail !== undefined ? (
        <a href={`mailto:${content.contactEmail}`} className="jt-help-pill jt-help-pill--white">
          Email us
        </a>
      ) : (
        <span className="jt-help-pill jt-help-pill--white jt-help-pill--off" aria-disabled="true">
          Email us <Soon />
        </span>
      )}
    </div>
  );
}

// -- Apps, feedback, social ------------------------------------------------------

const SOCIALS = [
  {
    n: 'Facebook',
    d: 'M15 3.5h-2.5A3.5 3.5 0 0 0 9 7v3H6.5v3.5H9V21h3.5v-7.5H15l.5-3.5h-3V7.5a1 1 0 0 1 1-1h1.5Z',
  },
  {
    n: 'Instagram',
    d: 'M7.5 3.5h9a4 4 0 0 1 4 4v9a4 4 0 0 1-4 4h-9a4 4 0 0 1-4-4v-9a4 4 0 0 1 4-4ZM12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7ZM17 7h.01',
  },
  { n: 'X', d: 'M4.5 4.5 19.5 19.5M19.5 4.5 4.5 19.5' },
  {
    n: 'YouTube',
    d: 'M3.5 8a3 3 0 0 1 3-3h11a3 3 0 0 1 3 3v8a3 3 0 0 1-3 3h-11a3 3 0 0 1-3-3ZM10 9.5v5l4.5-2.5Z',
  },
] as const;

function StoreButton({
  href,
  small,
  big,
  label,
}: {
  readonly href: string | undefined;
  readonly small: string;
  readonly big: string;
  readonly label: string;
}): JSX.Element {
  const inner = (
    <span className="jt-help-store__text">
      <span className="jt-help-store__small">{small}</span>
      <span className="jt-help-store__big">{big}</span>
    </span>
  );
  if (href !== undefined) {
    return (
      <a href={href} className="jt-help-store" target="_blank" rel="noopener noreferrer">
        {inner}
      </a>
    );
  }
  return (
    <span className="jt-help-store jt-help-store--off" aria-disabled="true" title={`${label} — coming soon`}>
      {inner}
      <span className="jt-visually-hidden"> (coming soon)</span>
    </span>
  );
}

/** The frame's grey band. Every control in it is inert until a real channel exists. */
function Extras(): JSX.Element {
  const apps = appStoreListings.ios === undefined && appStoreListings.android === undefined;
  return (
    <section className="jt-help-extras" aria-label="More from Just Treated">
      <div className="jt-help-extras__col">
        <h2 className="jt-help-extras__head">Download our apps</h2>
        <div className="jt-help-extras__row">
          <StoreButton
            href={appStoreListings.ios}
            small="Download on the"
            big="App Store"
            label="App Store"
          />
          <StoreButton
            href={appStoreListings.android}
            small="GET IT ON"
            big="Google Play"
            label="Google Play"
          />
        </div>
        {apps ? <Soon /> : null}
      </div>
      <div className="jt-help-extras__col">
        <h2 className="jt-help-extras__head">Feedback</h2>
        <span className="jt-help-extras__sub">Help us improve Just Treated</span>
        <span className="jt-help-extras__link" aria-disabled="true">
          Send feedback <Soon />
        </span>
      </div>
      <div className="jt-help-extras__col">
        <h2 className="jt-help-extras__head">Follow us</h2>
        <div className="jt-help-extras__row">
          {SOCIALS.map((s) => (
            <span
              key={s.n}
              className="jt-help-social"
              role="img"
              aria-label={`${s.n} — coming soon`}
              title={`${s.n} — coming soon`}
            >
              <Icon d={s.d} stroke="currentColor" size={17} />
            </span>
          ))}
        </div>
        <Soon />
      </div>
    </section>
  );
}

// -- Small parts -----------------------------------------------------------------

function Soon(): JSX.Element {
  return <span className="jt-help-soon">Coming soon</span>;
}

function Chevron({
  flipped = false,
  small = false,
}: {
  readonly flipped?: boolean;
  readonly small?: boolean;
}): JSX.Element {
  return (
    <svg
      className={`jt-help-chev${flipped ? ' jt-help-chev--up' : ''}`}
      width={small ? 15 : 16}
      height={small ? 15 : 16}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={small ? 2.4 : 2.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M6 9.5 12 15.5 18 9.5" />
    </svg>
  );
}

function Icon({
  d,
  stroke,
  size = 18,
  width = 2,
}: {
  readonly d: string;
  readonly stroke: string;
  readonly size?: number;
  readonly width?: number;
}): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={stroke}
      strokeWidth={width}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={d} />
    </svg>
  );
}
